'use client';

import React, { useEffect, useRef, useTransition } from 'react';
import Filter from './Filter';
import ArticleCard from './ArticleCard';
import { MobileFilter } from './MobileFilter';
import { CustomPagination } from '@/components/ui/new/CustomPagination';
import { useBlogContext } from '@/context/BlogContext';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { BlogListingSkeleton } from './BlogListingSkeleton';

const BlogListing = () => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const blogListRef = useRef(null);
  const [isPending, startTransition] = useTransition();

  const { limit, totalBlogsCount, blogs, currentPage, totalPages } =
    useBlogContext();

  const scrollToBlogStart = (callback) => {
    if (!blogListRef.current) {
      callback?.();
      return;
    }

    const rect = blogListRef.current.getBoundingClientRect();
    const headerOffset = 96; // same as scrollMarginTop

    const distance = Math.abs(rect.top - headerOffset);
    const alreadyAtStart = distance < 150; // threshold
    if (alreadyAtStart) {
      callback?.();
      return;
    }

    const y = rect.top + window.scrollY - headerOffset;

    // Ensures callback fires only once
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      callback?.();
    };

    // Fires when smooth scrolling fully completes
    const onScrollEnd = () => {
      window.removeEventListener('scrollend', onScrollEnd);
      finish();
    };
    window.addEventListener('scrollend', onScrollEnd);

    // Fallback: wait 2 frames so scroll *starts*, then add a timeout in case `scrollend` never fires on some devices/browsers.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(finish, 150); // fallback timeout
      });
    });

    window.scrollTo({
      top: y,
      behavior: 'smooth',
    });

    // Trigger scroll
    window.scrollTo({
      top: y,
      behavior: 'smooth',
    });
  };

  const handlePageChange = (page) => {
    scrollToBlogStart(() => {
      startTransition(() => {
        const params = new URLSearchParams(searchParams.toString());
        params.set('page', page);
        router.push(`${pathname}?${params.toString()}`, { scroll: false });
      });
    });
  };

  return (
    <>
      <div
        className="flex gap-12"
        ref={blogListRef}
        style={{ scrollMarginTop: '96px' }}
      >
        {/* Sidebar for Desktop */}
        <div className="hidden md:block top-0 sticky w-full max-w-[300px]">
          <Filter
            isSticky={true}
            isPending={isPending}
            startTransition={startTransition}
            scrollToBlogStart={scrollToBlogStart}
          />
        </div>
        <div className="flex flex-col w-full">
          <div className="flex justify-between items-center">
            <p className="font-inter-tight text-[#89D7DF] text-[18px] uppercase">
              All Articles
            </p>
            <MobileFilter
              isPending={isPending}
              startTransition={startTransition}
              scrollToBlogStart={scrollToBlogStart}
            />
          </div>

          <span className="hidden md:block opacity-60 mt-4 border-[#8AD6DE] border-b w-full h-[1px]"></span>
          {isPending ? (
            <BlogListingSkeleton count={Math.min(limit, blogs.length)} />
          ) : (
            <>
              <p className="my-6 font-inter-tight text-[#B3B3B3] text-[13px]">
                Showing {Math.min(limit, blogs.length)} of {totalBlogsCount}{' '}
                articles
              </p>
              <div className="grid lg:grid-cols-2 gap-6 w-full">
                {blogs.map((blog, idx) => {
                  return <ArticleCard key={idx} article={blog} />;
                })}
              </div>
            </>
          )}
        </div>
      </div>
      {blogs.length > 0 && (
        <div className="w-full">
          <CustomPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => handlePageChange(page)}
          />
        </div>
      )}
    </>
  );
};

export default BlogListing;
